import { ITaskController } from "../../controller";
import { TagType, Tag } from "shared/datamodel/schemas/metadata";
import { canvasMetadataPrefix } from "shared/util/utils";
import { useItemsFromReplicache } from "frontend/subscriptions";
import { useMemo } from "react";

const tagPrefix = `${canvasMetadataPrefix}${TagType}-`;

/**
 * Hook to get all tags from replicache, sorted by their order/lastModifiedTimestamp.
 * Used for tag editors and dropdowns where we want to show tags in replicache order.
 * @param controller - The task controller
 * @returns Array of [tagId, Tag] tuples for all tags in the canvas, sorted by order
 */
export function useAllTags(controller: ITaskController): [string, Tag][] {
  const allTagEntries = useItemsFromReplicache(controller.context.reflect, tagPrefix, []);

  const sortedEntries = useMemo(() => {
    const entries = [...(allTagEntries as [string, Tag][])];
    entries.sort((a, b) => {
      const aKey = a[1].order ?? a[1].lastModifiedTimestamp ?? 0;
      const bKey = b[1].order ?? b[1].lastModifiedTimestamp ?? 0;
      return aKey - bKey;
    });
    return entries;
  }, [allTagEntries]);

  return sortedEntries;
}

/**
 * Hook to get only the tags that are assigned to this task, in the order they appear on the task.
 * Automatically filters out tags that have been deleted from replicache.
 * Used for displaying tags in widgets and modals where we want to preserve the task's tag order.
 * @param controller - The task controller
 * @param limit - Optional limit on number of tags to return (for display purposes)
 * @returns Array of Tag objects that are both assigned to the task and exist in replicache, in task order
 */
export function useTaskTags(controller: ITaskController, limit?: number): Tag[] {
  const taskTagIds = controller.getSelectedTags();
  const allTagEntries = useItemsFromReplicache(controller.context.reflect, tagPrefix, []) as [string, Tag][];

  const filteredTags = useMemo(() => {
    if (!taskTagIds || taskTagIds.length === 0) return [];

    const tagMap = new Map<string, Tag>();
    for (const [key, tag] of allTagEntries) {
      const tagId = key.replace(tagPrefix, "");
      tagMap.set(tagId, tag);
    }

    const tags: Tag[] = [];
    for (const tagId of taskTagIds) {
      const tag = tagMap.get(tagId);
      if (tag) {
        tags.push(tag);
      }
    }

    return limit ? tags.slice(0, limit) : tags;
  }, [taskTagIds, allTagEntries, limit]);

  return filteredTags;
}

/**
 * Helper function to convert tag entries to a format compatible with legacy code
 * @param allTagEntries - Raw tag entries from replicache
 * @returns Array of [shortId, Tag] tuples with short IDs (without prefix)
 */
export function processTagEntries(allTagEntries: [string, Tag][]): [string, Tag][] {
  return allTagEntries.map(([key, tag]) => [key.replace(tagPrefix, ""), tag]);
}
